import { useEffect, useRef, useState } from "react";
import { ExternalLink, LoaderCircle, X } from "lucide-react";

type DocxPreviewDialogProps = {
  open: boolean;
  downloadUrl: string;
  onClose: () => void;
};

export function DocxPreviewDialog({
  open,
  downloadUrl,
  onClose,
}: DocxPreviewDialogProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !downloadUrl || !containerRef.current) return;
    let cancelled = false;
    const render = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(downloadUrl);
        if (!response.ok) throw new Error("无法读取导出的 DOCX");
        const blob = await response.blob();
        const { renderAsync } = await import("docx-preview");
        if (cancelled || !containerRef.current) return;
        containerRef.current.replaceChildren();
        await renderAsync(blob, containerRef.current, undefined, {
          inWrapper: true,
          breakPages: true,
          ignoreWidth: false,
          ignoreHeight: false,
          experimental: true,
        });
      } catch (reason) {
        if (!cancelled) {
          setError(reason instanceof Error ? reason.message : "预览失败");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void render();
    return () => {
      cancelled = true;
    };
  }, [downloadUrl, open]);

  if (!open) return null;
  return (
    <div className="modal-backdrop">
      <section className="docx-preview-dialog">
        <header>
          <div>
            <span className="eyebrow">DOCX-PREVIEW</span>
            <h2>导出文件快速预览</h2>
            <p>这是 HTML 近似渲染，不作为 Word 最终分页证明。</p>
          </div>
          <div>
            <a href={downloadUrl}>
              <ExternalLink size={14} /> 下载 DOCX
            </a>
            <button aria-label="关闭预览" onClick={onClose} type="button">
              <X size={19} />
            </button>
          </div>
        </header>
        {loading ? (
          <div className="preview-loading">
            <LoaderCircle className="spin" size={24} /> 正在解析 DOCX…
          </div>
        ) : null}
        {error ? <div className="preview-error">{error}</div> : null}
        <div className="docx-preview-host" ref={containerRef} />
      </section>
    </div>
  );
}
